import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const authAxios = axios.create();

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Dashboard
export const getDashboardOverview = () => authAxios.get(`${API}/dashboard/overview`);

// Risk
export const getRiskOverview = () => authAxios.get(`${API}/risk/overview`);
export const getDeviceRisks = () => authAxios.get(`${API}/risk/devices`);
export const getUserRisks = () => authAxios.get(`${API}/risk/users`);
export const explainRisk = (threatType, threatData) => 
  authAxios.post(`${API}/risk/explain`, { threat_type: threatType, threat_data: threatData });

// DNS
export const getDNSThreats = (limit = 50) => authAxios.get(`${API}/threats/dns?limit=${limit}`);
export const evaluateDNS = (domain, deviceId) => 
  authAxios.post(`${API}/dns/evaluate`, { domain, device_id: deviceId });
export const getDNSPolicies = () => authAxios.get(`${API}/dns/policies`);
export const createDNSPolicy = (policy) => authAxios.post(`${API}/dns/policies`, policy);

// Files
export const getFileThreats = (limit = 50) => authAxios.get(`${API}/threats/files?limit=${limit}`);
export const scanFile = (fileData) => authAxios.post(`${API}/files/scan`, fileData);
export const fileAction = (fileId, action) => 
  authAxios.put(`${API}/files/${fileId}/action`, { action });

// Emails
export const getEmailThreats = (limit = 50) => authAxios.get(`${API}/threats/emails?limit=${limit}`);
export const analyzeEmail = (emailData) => authAxios.post(`${API}/emails/analyze`, emailData);

// Apps
export const getAppThreats = (limit = 50) => authAxios.get(`${API}/threats/apps?limit=${limit}`);

// Devices
export const getDevices = () => authAxios.get(`${API}/devices`);
export const registerDevice = (deviceData) => authAxios.post(`${API}/devices/register`, deviceData);
export const deviceAction = (deviceId, action) => 
  authAxios.put(`${API}/devices/${deviceId}/action`, { action });

// Users
export const getUsers = () => authAxios.get(`${API}/auth/users`);
export const createUser = (userData) => authAxios.post(`${API}/auth/users`, userData);

// Automation
export const getAutomationRules = () => authAxios.get(`${API}/automation/rules`);
export const createAutomationRule = (rule) => authAxios.post(`${API}/automation/rules`, rule);
export const updateAutomationRule = (ruleId, rule) => 
  authAxios.put(`${API}/automation/rules/${ruleId}`, rule);
export const deleteAutomationRule = (ruleId) => 
  authAxios.delete(`${API}/automation/rules/${ruleId}`);
export const aiGenerateRule = (description) => 
  authAxios.post(`${API}/automation/ai-generate`, { description });
export const aiSuggestRules = () => authAxios.post(`${API}/automation/ai-suggest`);

// Reports
export const getReports = () => authAxios.get(`${API}/reports`);
export const generateReport = () => authAxios.post(`${API}/reports/generate`);

// Billing
export const getBillingSummary = () => authAxios.get(`${API}/billing/usage`);
export const getSeatUsage = () => authAxios.get(`${API}/billing/usage`);

// MSP Portal
export const getMSPTenants = () => authAxios.get(`${API}/msp/tenants`);
export const getMSPOverview = () => authAxios.get(`${API}/msp/overview`);
export const createMSPTenant = (tenantData) => authAxios.post(`${API}/msp/create-tenant`, tenantData);
export const getMSPTenantDetails = (tenantId) => authAxios.get(`${API}/msp/tenant/${tenantId}/details`);

// Settings
export const getSettings = () => authAxios.get(`${API}/settings`);
export const updateTenantSettings = (data) => authAxios.put(`${API}/settings/tenant`, data);
export const updateUserProfile = (data) => authAxios.put(`${API}/settings/profile`, data);
export const changePassword = (data) => authAxios.put(`${API}/settings/password`, data);
export const updateSecuritySettings = (data) => authAxios.put(`${API}/settings/security`, data);

// Global Admin
export const setupSuperAdmin = (data) => authAxios.post(`${API}/admin/setup`, data);
export const getAdminHealth = () => authAxios.get(`${API}/health`);
export const getAdminStats = () => authAxios.get(`${API}/admin/overview`);
export const getAdminTenants = (skip = 0, limit = 50) => authAxios.get(`${API}/admin/tenants?skip=${skip}&limit=${limit}`);
export const getAdminTenantById = (tenantId) => authAxios.get(`${API}/admin/tenants/${tenantId}`);
export const updateAdminTenant = (tenantId, data) => authAxios.put(`${API}/admin/tenants/${tenantId}`, data);
export const deleteAdminTenant = (tenantId) => authAxios.delete(`${API}/admin/tenants/${tenantId}`);
export const getAdminTenantUsers = (tenantId) => authAxios.get(`${API}/admin/tenants/${tenantId}/users`);
export const addAdminTenantUser = (tenantId, data) => authAxios.post(`${API}/admin/tenants/${tenantId}/users`, data);
export const deleteAdminUser = (userId) => authAxios.delete(`${API}/admin/users/${userId}`);
export const updateAdminUser = (userId, data) => authAxios.put(`${API}/admin/users/${userId}`, data);
export const resetAdminUserPassword = (userId) => authAxios.put(`${API}/admin/users/${userId}/reset-password`);
export const getAdminUserActivity = (userId) => authAxios.get(`${API}/admin/users/${userId}/activity`);

// Support Tickets (Admin)
export const getAdminSupportTickets = (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  return authAxios.get(`${API}/admin/support/tickets${queryParams ? '?' + queryParams : ''}`);
};
export const getAdminSupportStats = () => authAxios.get(`${API}/admin/support/stats`);
export const updateAdminSupportTicket = (ticketId, data) => authAxios.put(`${API}/admin/support/tickets/${ticketId}`, data);
export const getSupportAgents = () => authAxios.get(`${API}/admin/support/agents`);

// Support Tickets (User)
export const createSupportTicket = (data) => authAxios.post(`${API}/support/tickets`, data);
export const getMySupportTickets = () => authAxios.get(`${API}/support/tickets`);
export const getSupportTicketDetail = (ticketId) => authAxios.get(`${API}/support/tickets/${ticketId}`);
export const addTicketMessage = (ticketId, data) => authAxios.post(`${API}/support/tickets/${ticketId}/messages`, data);

// Endpoint Agent
export const getAgentInfo = () => authAxios.get(`${API}/agent/info`);
export const getAgentDownload = (platform) => authAxios.get(`${API}/agent/download/${platform}`);
export const getAgentDevices = () => authAxios.get(`${API}/agent/devices`);

// Agent Management
export const getAgents = () => authAxios.get(`${API}/agents`);
export const getAgentStats = () => authAxios.get(`${API}/agents/stats`);
export const generateEnrollmentToken = () => authAxios.post(`${API}/agents/enrollment-token`);
export const sendAgentCommand = (agentId, command) => authAxios.post(`${API}/agents/${agentId}/command`, { command });
export const getAgentUpdateStats = () => authAxios.get(`${API}/agents/updates/stats`);
export const getAgentReleases = () => authAxios.get(`${API}/agents/updates/releases`);

// Threat Intelligence
export const getThreatFeeds = () => authAxios.get(`${API}/threat-intel/feeds`);
export const getThreatIntelStats = () => authAxios.get(`${API}/threat-intel/stats`);
export const enableThreatFeed = (feedId) => authAxios.post(`${API}/threat-intel/feeds/${feedId}/enable`);
export const disableThreatFeed = (feedId) => authAxios.post(`${API}/threat-intel/feeds/${feedId}/disable`);
export const syncThreatFeed = (feedId) => authAxios.post(`${API}/threat-intel/feeds/${feedId}/sync`);
export const lookupThreatIndicator = (data) => authAxios.post(`${API}/threat-intel/lookup`, data);
export const getDNSBlocklist = () => authAxios.get(`${API}/threat-intel/dns-blocklist`);
export const getHashBlocklist = () => authAxios.get(`${API}/threat-intel/hash-blocklist`);
export const getIPBlocklist = () => authAxios.get(`${API}/threat-intel/ip-blocklist`);

// AI Analysis
export const getAIStatus = () => authAxios.get(`${API}/ai/status`);
export const analyzeThreathWithAI = (threatType, threatData) => 
  authAxios.post(`${API}/ai/analyze-threat`, { threat_type: threatType, threat_data: threatData });
export const getDashboardAIInsights = () => authAxios.get(`${API}/ai/dashboard-insights`);

// =============================================================================
// MSP (Managed Service Provider) APIs
// =============================================================================

// MSP Auth
export const mspSignup = (data) => authAxios.post(`${API}/msp/signup`, data);
export const mspLogin = (data) => authAxios.post(`${API}/msp/login`, data);
export const getMSPProfile = () => authAxios.get(`${API}/msp/me`);

// MSP Dashboard
export const getMSPDashboard = () => authAxios.get(`${API}/msp/dashboard`);

// MSP Clients
export const getMSPClients = () => authAxios.get(`${API}/msp/clients`);
export const createMSPClient = (data) => authAxios.post(`${API}/msp/clients`, data);
export const getMSPClientDetail = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}`);
export const updateMSPClient = (clientId, data) => authAxios.put(`${API}/msp/clients/${clientId}`, data);

// MSP Client User Management
export const getMSPClientUsers = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}/users`);
export const addMSPClientUser = (clientId, data) => authAxios.post(`${API}/msp/clients/${clientId}/users`, data);
export const removeMSPClientUser = (clientId, userId) => authAxios.delete(`${API}/msp/clients/${clientId}/users/${userId}`);
export const resetMSPClientUserPassword = (clientId, userId) => authAxios.post(`${API}/msp/clients/${clientId}/users/${userId}/reset-password`);

// MSP Client Device Management
export const getMSPClientDevices = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}/devices`);
export const mspClientDeviceAction = (clientId, deviceId, action) => authAxios.put(`${API}/msp/clients/${clientId}/devices/${deviceId}/action`, { action });

// MSP Client Automation Management
export const getMSPClientAutomationRules = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}/automation/rules`);
export const createMSPClientAutomationRule = (clientId, data) => authAxios.post(`${API}/msp/clients/${clientId}/automation/rules`, data);
export const updateMSPClientAutomationRule = (clientId, ruleId, data) => authAxios.put(`${API}/msp/clients/${clientId}/automation/rules/${ruleId}`, data);
export const deleteMSPClientAutomationRule = (clientId, ruleId) => authAxios.delete(`${API}/msp/clients/${clientId}/automation/rules/${ruleId}`);

// MSP Client DNS Policy Management
export const getMSPClientDNSPolicies = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}/dns/policies`);
export const createMSPClientDNSPolicy = (clientId, data) => authAxios.post(`${API}/msp/clients/${clientId}/dns/policies`, data);
export const deleteMSPClientDNSPolicy = (clientId, policyId) => authAxios.delete(`${API}/msp/clients/${clientId}/dns/policies/${policyId}`);

// MSP Client Security Settings
export const getMSPClientSecuritySettings = (clientId) => authAxios.get(`${API}/msp/clients/${clientId}/settings/security`);
export const updateMSPClientSecuritySettings = (clientId, data) => authAxios.put(`${API}/msp/clients/${clientId}/settings/security`, data);

// MSP Permissions
export const getMSPPermissions = () => authAxios.get(`${API}/msp/permissions`);

// MSP Support Tickets
export const getMSPSupportTickets = () => authAxios.get(`${API}/msp/support/tickets`);
export const getMSPTicketDetail = (ticketId) => authAxios.get(`${API}/msp/support/tickets/${ticketId}`);
export const updateMSPTicket = (ticketId, data) => authAxios.put(`${API}/msp/support/tickets/${ticketId}`, data);
export const replyToMSPTicket = (ticketId, data) => authAxios.post(`${API}/msp/support/tickets/${ticketId}/reply`, data);
export const getMSPSupportTeam = () => authAxios.get(`${API}/msp/support/team`);

// MSP Team
export const getMSPTeam = () => authAxios.get(`${API}/msp/team`);
export const addMSPTeamMember = (data) => authAxios.post(`${API}/msp/team`, data);
export const updateMSPTeamMember = (memberId, data) => authAxios.put(`${API}/msp/team/${memberId}`, data);
export const removeMSPTeamMember = (memberId) => authAxios.delete(`${API}/msp/team/${memberId}`);

// MSP Earnings
export const getMSPEarnings = () => authAxios.get(`${API}/msp/earnings`);

// MSP Stripe
export const connectMSPStripe = (data) => authAxios.post(`${API}/msp/stripe/connect`, data);
export const disconnectMSPStripe = () => authAxios.post(`${API}/msp/stripe/disconnect`);

// MSP Settings
export const getMSPSettings = () => authAxios.get(`${API}/msp/settings`);
export const updateMSPSettings = (data) => authAxios.put(`${API}/msp/settings`, data);

// Admin MSP Management
export const getAdminMSPs = () => authAxios.get(`${API}/admin/msps`);
export const getAdminMSPById = (mspId) => authAxios.get(`${API}/admin/msps/${mspId}`);
export const updateAdminMSP = (mspId, data) => authAxios.put(`${API}/admin/msps/${mspId}`, data);

// Webhooks
export const getWebhooks = () => authAxios.get(`${API}/webhooks`);
export const createWebhook = (data) => authAxios.post(`${API}/webhooks`, data);
export const updateWebhook = (webhookId, data) => authAxios.put(`${API}/webhooks/${webhookId}`, data);
export const deleteWebhook = (webhookId) => authAxios.delete(`${API}/webhooks/${webhookId}`);
export const testWebhook = (webhookId) => authAxios.post(`${API}/webhooks/${webhookId}/test`);
export const getWebhookLogs = (webhookId) => authAxios.get(`${API}/webhooks/${webhookId}/logs`);

// MSP Webhooks
export const getMSPWebhooks = () => authAxios.get(`${API}/msp/webhooks`);
export const createMSPWebhook = (data) => authAxios.post(`${API}/msp/webhooks`, data);
export const deleteMSPWebhook = (webhookId) => authAxios.delete(`${API}/msp/webhooks/${webhookId}`);

// Notifications
export const getNotifications = (limit = 50, unreadOnly = false) => 
  authAxios.get(`${API}/notifications?limit=${limit}&unread_only=${unreadOnly}`);
export const markNotificationsRead = (notificationIds) => 
  authAxios.post(`${API}/notifications/mark-read`, { notification_ids: notificationIds });
export const markAllNotificationsRead = () => authAxios.post(`${API}/notifications/mark-all-read`);
export const deleteNotification = (notificationId) => authAxios.delete(`${API}/notifications/${notificationId}`);
export const sendTestNotification = () => authAxios.post(`${API}/notifications/test`);

// MSP Notifications
export const getMSPNotifications = (limit = 50, unreadOnly = false) => 
  authAxios.get(`${API}/msp/notifications?limit=${limit}&unread_only=${unreadOnly}`);
export const markAllMSPNotificationsRead = () => authAxios.post(`${API}/msp/notifications/mark-all-read`);

// SSE Streams
export const getNotificationStreamURL = () => `${API}/notifications/stream`;
export const getMSPNotificationStreamURL = () => `${API}/msp/notifications/stream`;
